import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Input, Skeleton, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import '@fontsource/rubik';

import AuthLayout from '../layouts/AuthLayout';
import Header from '../userfiles/components/Header';
import KanbanCard from '../dashboard/components/KanbanCard';
import OrgSelectorDropdown from '../dashboard/components/OrgSelectorDropdown';
import PharoahVisibilityUndoSnackbar from '../../components/PharoahVisibilityUndoSnackbar';
import { emptyStateMessage, shouldShowEmptyState, shouldShowStateChip } from './viewState';
import useDebounce from '../dashboard/hooks/useDebounce';
import { readSearchParam, useSyncSearchParam } from '../../hooks/useSearchStringParam';
import useHiddenProjectsActions from './useHiddenProjectsActions';
import { updateState } from '../../redux/commonSlice';
import { updateReviewState } from '../../redux/reviewSlice';
import { updateProjectState } from '../../redux/projectSlice';

const SKELETON_COUNT = 6;

const CARD_WIDTH = {
  xs: '100%',
  sm: 'calc(50% - 12px)',
  md: 'calc(33.333% - 12px)',
  lg: 'calc(25% - 12px)',
};

/**
 * Hidden projects — Admin-only review queue backed by GET /api/v1/events/hidden.
 * A flat list rather than a staged board; the card shape mirrors board_column so
 * the board card renders it unchanged (including its Unhide action).
 */
const HiddenProjects = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Bumped by usePharoahVisibility whenever a project becomes hidden — including an
  // undo of a "show" done from this very list, which has to come back into the queue.
  const hiddenListRevision = useSelector((state) => state.root.commonReducer?.pharoah_hidden_list_revision);

  // The search text lives in the URL so that opening a project from this queue and
  // coming back restores the list as it was, not an unsearched one.
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchString, setSearchString] = useState(() => readSearchParam(searchParams));
  const debouncedSearch = useDebounce(searchString, 500);
  useSyncSearchParam(searchParams, setSearchParams, debouncedSearch);
  const [selectedOrganisation, setSelectedOrganisation] = useState('');

  const {
    getHiddenProjects,
    loadNextPage,
    removeProject,
    events,
    isLoading,
    isError,
    hasLoaded,
    totalCount,
    hasNextPage,
  } = useHiddenProjectsActions();

  useEffect(() => {
    getHiddenProjects({
      organisationId: selectedOrganisation?.id,
      name: debouncedSearch || undefined,
    });
  }, [debouncedSearch, selectedOrganisation?.id, hiddenListRevision, getHiddenProjects]);

  // Same navigation contract as the board — the detail screen still opens for a
  // hidden project, which is what lets this list link into it.
  const onCardClick = useCallback(
    (event) => {
      const canUpload = event?.flag?.can_upload ?? event?.can_upload;
      dispatch(updateState({ production_event_id: event?.id, can_upload: canUpload }));
      dispatch(updateReviewState({ can_upload: canUpload }));
      dispatch(
        updateProjectState({
          event_organisation_name: event?.organisation_name ?? null,
          event_tags: Array.isArray(event?.tags) ? event.tags : [],
          event_video_length: event?.video_length ?? null,
          event_video_orientation: event?.video_orientation ?? null,
        })
      );
      // Hand the detail screen this list's URL so Back returns to the hidden queue
      // rather than dropping the user on the main board.
      navigate(`/completedevent/${event?.organisation_id}/${event?.id}`, {
        state: { due_date: event?.due_date, returnTo: `${location.pathname}${location.search || ''}` },
      });
    },
    [dispatch, navigate, location.pathname, location.search]
  );

  const viewState = { hasLoaded, isLoading, isError, count: events.length };
  const showEmptyState = shouldShowEmptyState(viewState);
  const showStateChip = shouldShowStateChip(viewState);

  return (
    <AuthLayout sx={{ padding: '2.1% 3.4% 2.1% 3.4%' }}>
      <Header />
      <Box px={{ xs: 1.5, sm: 2, md: 3 }} pr={{ xs: 1.5, sm: 3, md: 6 }} className="flex col" mt={4}>
        <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', color: 'black', mb: 2 }} gap={1}>
          <KeyboardBackspaceOutlinedIcon onClick={() => navigate(-1)} sx={{ color: 'black', cursor: 'pointer' }} />
          <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '10px', sm: '11px', md: '12px', lg: '14px', xl: '20px' } }}>
            Back
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 1.5, md: 4 },
            width: '100%',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityOffOutlinedIcon sx={{ fontSize: { xs: '18px', md: '22px', lg: '26px', xl: '36px' }, color: '#5A6474' }} />
            <Box>
              <Typography
                sx={{
                  color: '#000000',
                  fontFamily: 'Rubik',
                  fontWeight: 600,
                  fontSize: { xs: '16px', md: '18px', lg: '20px', xl: '30px' },
                }}
              >
                Hidden Projects
              </Typography>
              <Typography
                sx={{ color: '#000000', fontFamily: 'Rubik', fontSize: { xs: '11px', md: '12px', lg: '13px', xl: '20px' } }}
              >
                Projects hidden from the dashboard for everyone. Clients still see them in QueenBee.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'end',
              gap: { xs: 1, sm: 2 },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <OrgSelectorDropdown
              selectedOrganisation={selectedOrganisation}
              setSelectedOrganisation={setSelectedOrganisation}
            />
            <Box id="search">
              <Input
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
                inputProps={{ maxLength: 100 }}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  borderBottom: 2,
                  borderColor: '#333333',
                  input: {
                    fontSize: { xs: '12px', md: '13px', lg: '14px', xl: '20px' },
                    fontWeight: 400,
                    color: '#000000',
                  },
                }}
                placeholder="Search"
                startAdornment={
                  <IconButton
                    sx={{
                      paddingBottom: 1,
                      fontSize: { xs: '12px', md: '14px', lg: '17px', xl: '24px' },
                      '& .MuiSvgIcon-root': { fontSize: { xs: '12px', md: '14px', lg: '17px', xl: '24px' } },
                      paddingRight: 2,
                    }}
                  >
                    <SearchIcon style={{ color: '#000000' }} />
                  </IconButton>
                }
              />
            </Box>
          </Box>
        </Box>

        {/* State chip — this list looks like any other project list otherwise. Hidden
            when there is nothing to show; see showStateChip. */}
        {showStateChip && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Chip
            icon={<VisibilityOffOutlinedIcon sx={{ fontSize: '13px !important', color: '#5A6474 !important' }} />}
            label="Showing hidden projects only"
            size="small"
            data-testid="hidden-projects-banner"
            sx={{
              fontFamily: 'Rubik',
              fontWeight: 600,
              fontSize: { xs: '9.5px', lg: '11px' },
              color: '#5A6474',
              backgroundColor: '#EEF0F4',
              border: '1px solid #D7DBE3',
              borderRadius: '6px',
              height: '22px',
              '& .MuiChip-label': { px: '6px' },
            }}
          />
          {hasLoaded && !isError && (
            <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '10px', lg: '11px', xl: '14px' }, color: '#8A909C' }}>
              {totalCount} {totalCount === 1 ? 'project' : 'projects'}
            </Typography>
          )}
        </Box>
        )}

        {isError && !isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, flexDirection: 'column' }}>
            <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '12px', lg: '13px' }, color: 'error.main' }}>
              Failed to load hidden projects.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                getHiddenProjects({ organisationId: selectedOrganisation?.id, name: debouncedSearch || undefined })
              }
              sx={{ fontFamily: 'Rubik', textTransform: 'none', fontSize: { xs: '11px', lg: '12px' } }}
            >
              Try again
            </Button>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'stretch' }}>
          {isLoading && events.length === 0
            ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                <Box key={i} sx={{ width: CARD_WIDTH }}>
                  <Skeleton
                    variant="rounded"
                    animation="pulse"
                    sx={{ borderRadius: '10px', height: { xs: '189px', lg: '204px', xl: '254px' } }}
                  />
                </Box>
              ))
            : events.map((project) => (
                <Box key={project.id} sx={{ width: CARD_WIDTH }}>
                  <KanbanCard
                    project={project}
                    onClick={() => onCardClick(project)}
                    onVisibilityChange={(eventId, nextHidden) => {
                      // Shown on the dashboard again — it leaves this queue.
                      if (!nextHidden) removeProject(eventId);
                    }}
                  />
                </Box>
              ))}
        </Box>

        {showEmptyState && (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 1 }}
          >
            <InboxOutlinedIcon sx={{ fontSize: '34px', color: '#D0D3DB' }} />
            <Typography
              sx={{ fontSize: { xs: '12px', lg: '13px' }, fontFamily: 'Rubik', color: '#B0B3BC', fontWeight: 500 }}
            >
              {emptyStateMessage({ search: debouncedSearch, organisationId: selectedOrganisation?.id })}
            </Typography>
          </Box>
        )}

        {hasNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={isLoading}
              onClick={loadNextPage}
              startIcon={isLoading ? <CircularProgress size={14} /> : null}
              sx={{ fontFamily: 'Rubik', textTransform: 'none', fontSize: { xs: '11px', lg: '12px' } }}
            >
              Load more
            </Button>
          </Box>
        )}
      </Box>

      <PharoahVisibilityUndoSnackbar />
    </AuthLayout>
  );
};

export default HiddenProjects;
